import type { components } from "../../../api/generated";
import type {
  LegacySyncReviewItem,
  LegacySyncRunResult,
  LegacySyncTarget,
  LegacySyncTargetList,
} from "../types";

export type LegacySyncTargetRaw = {
  target_key: string;
  target_name: string;
  target_table: string;
  database_name: string;
  profile_name?: string;
  source_database_name?: string;
  target_database_name?: string;
  vector_store_name?: string | null;
  vector_base_url?: string | null;
  vector_update_path?: string | null;
  legacy_key: string;
  supports_preview: boolean;
  supports_execute: boolean;
  next_stage: string;
  last_success_source_updated_at?: string | null;
};

export type LegacySyncTargetListRaw = {
  items: LegacySyncTargetRaw[];
};

export type LegacySyncReviewItemRaw = {
  legacy_id?: string | null;
  reason_code: string;
  reason: string;
  source_name?: string | null;
  target_resource_id?: string | null;
  target_name?: string | null;
};

export type LegacySyncRunResultRaw = {
  target_key: string;
  mode: "dry_run" | "execute";
  sync_mode?: "full" | "incremental";
  status:
    | "not_implemented"
    | "not_enabled"
    | "planned"
    | "started"
    | "completed"
    | "blocked"
    | "paused"
    | "partial_failed"
    | "failed";
  summary: {
    total_source: number;
    planned_create: number;
    planned_update: number;
    skipped: number;
    conflicts: number;
    failed: number;
  };
  message: string;
  business_result?: components["schemas"]["LegacySyncBusinessResult"] | null;
  run_id?: string | null;
  cursor_from?: string | null;
  cursor_to?: string | null;
  profile_name?: string;
  source_database_name?: string;
  target_database_name?: string;
  vector_store_name?: string | null;
  vector_base_url?: string | null;
  review_items?: LegacySyncReviewItemRaw[];
  plan_id?: string | null;
  next_after_key?: string | null;
  has_more?: boolean;
  committed_groups?: number;
  reused_groups?: number;
};

/** 映射旧系统同步对象列表。 */
export function mapLegacySyncTargetList(raw: LegacySyncTargetListRaw): LegacySyncTargetList {
  return {
    items: (raw.items ?? []).map(mapLegacySyncTarget),
  };
}

/** 映射旧系统同步运行结果。 */
export function mapLegacySyncRunResult(raw: LegacySyncRunResultRaw): LegacySyncRunResult {
  return {
    targetKey: raw.target_key,
    mode: raw.mode,
    syncMode: raw.sync_mode ?? "full",
    status: raw.status,
    message: raw.message,
    businessResult: raw.business_result ? {
      total: raw.business_result.total ?? 0,
      ready: raw.business_result.ready ?? 0,
      existing: raw.business_result.existing ?? 0,
      synced: raw.business_result.synced ?? 0,
      pending: raw.business_result.pending ?? 0,
      failed: raw.business_result.failed ?? 0,
      pendingItems: (raw.business_result.pending_items ?? []).map((item) => ({
        legacyId: item.legacy_id, sourceName: item.source_name ?? null,
        reason: item.reason, needsPreview: item.needs_preview ?? false,
      })),
    } : null,
    runId: raw.run_id,
    planId: raw.plan_id,
    nextAfterKey: raw.next_after_key,
    hasMore: raw.has_more,
    committedGroups: raw.committed_groups,
    reusedGroups: raw.reused_groups,
    cursorFrom: raw.cursor_from,
    cursorTo: raw.cursor_to,
    profileName: raw.profile_name ?? "prod",
    sourceDatabaseName: raw.source_database_name ?? "",
    targetDatabaseName: raw.target_database_name ?? "",
    vectorStoreName: raw.vector_store_name ?? null,
    vectorBaseUrl: raw.vector_base_url ?? null,
    reviewItems: (raw.review_items ?? []).map(mapLegacySyncReviewItem),
    summary: {
      totalSource: raw.summary.total_source,
      plannedCreate: raw.summary.planned_create,
      plannedUpdate: raw.summary.planned_update,
      skipped: raw.summary.skipped,
      conflicts: raw.summary.conflicts,
      failed: raw.summary.failed,
    },
  };
}

/** 映射独立于技术冲突统计的业务人工复核明细。 */
function mapLegacySyncReviewItem(raw: LegacySyncReviewItemRaw): LegacySyncReviewItem {
  return {
    legacyId: raw.legacy_id ?? null,
    reasonCode: raw.reason_code,
    reason: raw.reason,
    sourceName: raw.source_name ?? null,
    targetResourceId: raw.target_resource_id ?? null,
    targetName: raw.target_name ?? null,
  };
}

/** 映射旧系统同步对象。 */
function mapLegacySyncTarget(raw: LegacySyncTargetRaw): LegacySyncTarget {
  return {
    targetKey: raw.target_key,
    targetName: raw.target_name,
    targetTable: raw.target_table,
    databaseName: raw.database_name,
    profileName: raw.profile_name ?? "prod",
    sourceDatabaseName: raw.source_database_name ?? "",
    targetDatabaseName: raw.target_database_name ?? raw.database_name,
    vectorStoreName: raw.vector_store_name ?? null,
    vectorBaseUrl: raw.vector_base_url ?? null,
    vectorUpdatePath: raw.vector_update_path ?? null,
    legacyKey: raw.legacy_key,
    supportsPreview: raw.supports_preview,
    supportsExecute: raw.supports_execute,
    nextStage: raw.next_stage,
    lastSuccessSourceUpdatedAt: raw.last_success_source_updated_at,
  };
}
